import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'utils/modifiedTranslation';
import { CALL_US_BY_DISEASE, DEFAULT_CALL_US } from 'utils/constants/callUs';
import { AFTER_SIGNUP_URL_SEARCH_PARAM } from 'utils/constants/urlSearchParameters';
import { POPUP } from 'utils/constants/popup';
import app from 'new-ui/app';
import './CallUs.css';
import { ReactComponent as CallIcon } from 'assets/icons/call_icon.svg';
import { ReactComponent as MenuIcon } from 'assets/icons/menu_board.svg';
import { ReactComponent as MessageIcon } from 'assets/icons/message_text.svg';
import Button from 'new-ui/Components/Button';
import { GA_CATEGORY_CALL_US_POPUP_NAME, callUsPopupEvents } from 'modules/ga/popups';
import { isCrmChatAllowed, isCurrentTimeInESTWorkingRange, isMobileWidth } from 'new-ui/functions';
import { CRM_EVENTS } from 'new-ui/constants';
import { openZendeskChat } from 'functions';
import { Preloader } from 'components/Preloader';
import { CrmChatAPI } from 'modules/crmChat';
import { intercomSignupEvent } from 'modules/intercom/intercom';
import { authenticationApi } from 'components/Authentication/api';
import { selectIsIntercomPopupInDOM } from 'modules/intercom/intercom.selectors';
import { selectIsAnyPopupShown } from 'modules/popup/popup.selectors';

const {
  talkToUsClicked,
  contactUsPoppedAuto,
  contactUsPopupViewed,
  callUsClicked,
} = callUsPopupEvents;

export const openCallUsPopup = () => {
  app.callUsPopupPopped = true;
  app.openPopup(POPUP.CALLUS, {
    CSS: {
      width: `${document.querySelector('.item-box')?.getBoundingClientRect().width || 600}px`,
    },
  });
};

export const setCallUsPopup = () => {
  const params = new URLSearchParams(window.location.search);
  const isIntercomPopupInDOM = selectIsIntercomPopupInDOM(app.store.getState());
  const isAnyPopupShown = selectIsAnyPopupShown(app.store.getState());
  if (!isIntercomPopupInDOM && !isAnyPopupShown && !app.callUsPopupPopped) {
    openCallUsPopup();
  } else if (!app.callUsPopupPopped) {
    if (params.get(AFTER_SIGNUP_URL_SEARCH_PARAM)) {
      params.delete(AFTER_SIGNUP_URL_SEARCH_PARAM);
      intercomSignupEvent();
    }
  }
};

let userIntrractedCallUsPopup = false;

const CallUs = ({ automatically }) => {
  const { t } = useTranslation();
  const [phone, setPhone] = useState(null);
  const [popupVisible, setPopupVisability] = useState(false);
  const [crmVisible, setCrmVisible] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    userIntrractedCallUsPopup = false;

    if (app.callusTimerId) {
      clearTimeout(app.callusTimerId);
    }
    if (isCrmChatAllowed()) {
      CrmChatAPI.showLauncher();
      if (app.setCrmChatVisible) {
        app.setCrmChatVisible(true);
        const crmLauncher = document.getElementById('launcher');
        if (crmLauncher) {
          crmLauncher.style.zIndex = '-1';
        }
      }
    }

    setTimeout(() => {
      setPopupVisability(true);
      setCrmVisible(app.crmVisibility || app.webWidgetVisibility);
    }, 500);

    if (automatically) {
      app.sendGoogleAnalyticsEvent(
        GA_CATEGORY_CALL_US_POPUP_NAME,
        contactUsPoppedAuto.eventName,
        contactUsPoppedAuto.getMetaData(),
      );
    } else {
      app.sendGoogleAnalyticsEvent(
        GA_CATEGORY_CALL_US_POPUP_NAME,
        talkToUsClicked.eventName,
        talkToUsClicked.getMetaData(),
      );
    }
    app.sendGoogleAnalyticsEvent(
      GA_CATEGORY_CALL_US_POPUP_NAME,
      contactUsPopupViewed.eventName,
      contactUsPopupViewed.getMetaData(),
    );
    return () => {
      if (!userIntrractedCallUsPopup && isCrmChatAllowed()) {
        CrmChatAPI.hideLauncher();

        app.callusTimerId = setTimeout(() => {
          if (!(app.crmVisibility || app.webWidgetVisibility)) {
            if (params.get(AFTER_SIGNUP_URL_SEARCH_PARAM)) {
              params.delete(AFTER_SIGNUP_URL_SEARCH_PARAM);
              intercomSignupEvent();
            }
          }
        }, 15000);
      }
      if (!userIntrractedCallUsPopup && !isCrmChatAllowed()) {
        app.callusTimerId = setTimeout(() => {
          if (params.get(AFTER_SIGNUP_URL_SEARCH_PARAM)) {
            params.delete(AFTER_SIGNUP_URL_SEARCH_PARAM);
            intercomSignupEvent();
          }
        }, 15000);
      }
    };
  }, [automatically]);

  const callUs = () => {
    userIntrractedCallUsPopup = true;
    app.sendGoogleAnalyticsEvent(
      GA_CATEGORY_CALL_US_POPUP_NAME,
      callUsClicked.eventName,
      callUsClicked.getMetaData(),
    );

    const phoneToCall = CALL_US_BY_DISEASE[app.user.personal?.condition]?.tel ?? DEFAULT_CALL_US.tel;
    if (isMobileWidth() && phoneToCall) {
      window.location.href = `tel:${phoneToCall.replace(/[\s-]+/g, '')}`;
    } else {
      setPhone(phoneToCall);
    }

    const user = app.user.info;
    const { family_name, email } = user;
    authenticationApi
      .registerUserRequests({
        user: {
          email,
          name: family_name ?? email,
        },
        type: CRM_EVENTS.CALL_US,
        content: 'The patient called us',
      }).catch((ex) => console.error(ex));

    authenticationApi.updateIntercomUser({
      data: {
        tags: ['called_us'],
      },
    }).catch((ex) => console.error(ex));
  };

  const shaduleACall = () => {
    userIntrractedCallUsPopup = true;
    if (!app.user.info.phone) {
      app.openPopup(POPUP.PHONE_NUMBER, {
        closePopup: () => {
          app.openPopup(POPUP.CONSULT_WITH_US);
        },
      });
    } else {
      app.openPopup(POPUP.CONSULT_WITH_US);
    }
  };

  const chatWithUs = () => {
    userIntrractedCallUsPopup = true;
    setTimeout(() => {
      if (app.crmVisibility || app.webWidgetVisibility) {
        const crmLauncher = document.getElementById('launcher');
        if (crmLauncher) {
          crmLauncher.style.zIndex = '9999';
        }
        openZendeskChat();
      }
    }, 0);

    app.closePopups();
    authenticationApi.updateIntercomUser({
      data: {
        tags: ['zendesk_chat_started'],
      },
    }).catch((ex) => console.error(ex));
  };

  return !popupVisible ? <Preloader isVisible /> : (
    <div className="call-us">
      <h2>{t('call_us_popup.title')}</h2>
      <p>{t(isCurrentTimeInESTWorkingRange() ? 'call_us_popup.subtitle' : 'call_us_popup.outofwork.subtitle')}</p>
      <div className="call-us-buttons">
        {
          isCurrentTimeInESTWorkingRange()
        && (
          phone
            ? (
              <p className="phone-number">
                Tel:
                {' '}
                {phone}
                {' '}
              </p>
            ) : (
              <Button
                svg={(<CallIcon />)}
                className="call-us-btn"
                title={t('call_us_popup.call_us.btn_title')}
                action={callUs}
              />
            )
        )
        }
        <Button
          svg={(<MenuIcon />)}
          className="shadule-a-call-btn"
          title={t('call_us_popup.shadule_a_cal.btn_title')}
          action={shaduleACall}
        />
        {
          isCurrentTimeInESTWorkingRange()
        && isCrmChatAllowed() && crmVisible
        && (
          <Button
            svg={(<MessageIcon />)}
            className="chat-with-us-btn"
            title={t('call_us_popup.chat_with_us.btn_title')}
            action={chatWithUs}
          />
        )
        }
      </div>
    </div>
  );
};

CallUs.propTypes = {
  automatically: PropTypes.bool,
};

export default CallUs;
